import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'react-hot-toast';
import { Package, ArrowLeft, Plus, Loader2, Box, Weight, Ruler, Check, Truck, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HU_TYPES = [
  { code: 'PAL', name: 'Pallet' },
  { code: 'BOX', name: 'Box' },
  { code: 'CTN', name: 'Carton' },
  { code: 'CRT', name: 'Crate' },
  { code: 'CON', name: 'Container' },
  { code: 'DRM', name: 'Drum' },
  { code: 'BAG', name: 'Bag' },
  { code: 'OTH', name: 'Other' }
];

const PackingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedPickList = searchParams.get('pickList');
  const preSelectedDelivery = searchParams.get('delivery');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [packingItems, setPackingItems] = useState([]);
  const [handlingUnits, setHandlingUnits] = useState([]);
  const [existingPacking, setExistingPacking] = useState(null);

  // New HU form
  const [newHU, setNewHU] = useState({
    hu_type: 'BOX',
    length: '',
    width: '',
    height: '',
    gross_weight: '',
    net_weight: '',
    packaging_material: '',
    selectedItems: {}
  });


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load deliveries that are ready for packing (picked but not packed)
      const response = await axios.get(`${BACKEND_URL}/api/otc/deliveries`);
      
      // Filter deliveries that are picked and ready for packing
      const eligibleDeliveries = (response.data || []).filter(d => 
        d.status === 'CREATED' && 
        (d.picking_status === 'PICKED' || d.picking_status === 'PARTIALLY_PICKED' || !d.picking_status)
      );
      
      setDeliveries(eligibleDeliveries);

      // If pre-selected delivery, load it
      if (preSelectedDelivery) {
        const delivery = eligibleDeliveries.find(d => d.delivery_number === preSelectedDelivery);
        if (delivery) {
          await loadDeliveryPacking(delivery);
        }
      } else if (preSelectedPickList) {
        // Load pick list and get deliveries from it
        try {
          const plRes = await axios.get(`${BACKEND_URL}/api/warehouse/pick-lists/${preSelectedPickList}`);
          if (plRes.data?.delivery_numbers?.length > 0) {
            const firstDeliveryNum = plRes.data.delivery_numbers[0];
            const delivery = eligibleDeliveries.find(d => d.delivery_number === firstDeliveryNum);
            if (delivery) {
              await loadDeliveryPacking(delivery);
            }
          }
        } catch (err) {
          console.error('Error loading pick list:', err);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setIsLoading(false);
    }
  }, [preSelectedDelivery, preSelectedPickList]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadDeliveryPacking = async (delivery) => {
    setSelectedDelivery(delivery);
    
    // Set packing items from delivery
    const items = (delivery.items || []).map(item => ({
      ...item,
      quantity: item.delivery_quantity || item.quantity || 0,
      packed_quantity: 0,
      remaining_quantity: item.delivery_quantity || item.quantity || 0
    }));
    setPackingItems(items);

    // Load existing packing if any
    try {
      const packingRes = await axios.get(`${BACKEND_URL}/api/warehouse/packing/${delivery.delivery_number}`);
      if (packingRes.data) {
        setExistingPacking(packingRes.data);
        setHandlingUnits(packingRes.data.handling_units || []);
        
        // Update packed quantities
        const updatedItems = items.map(item => {
          const packedQty = (packingRes.data.handling_units || []).reduce((sum, hu) => {
            const content = hu.contents?.find(c => c.delivery_item === item.item_number);
            return sum + (content?.quantity || 0);
          }, 0);
          const totalQty = item.delivery_quantity || item.quantity || 0;
          return {
            ...item,
            quantity: totalQty,
            packed_quantity: packedQty,
            remaining_quantity: totalQty - packedQty
          };
        });
        setPackingItems(updatedItems);
      }
    } catch (err) {
      // No existing packing
      setExistingPacking(null);
      setHandlingUnits([]);
    }
  };

  const handleItemSelect = (itemNumber, quantity) => {
    setNewHU(prev => ({
      ...prev,
      selectedItems: {
        ...prev.selectedItems,
        [itemNumber]: quantity
      }
    }));
  };

  const handleCreateHU = async () => {
    const selectedItems = Object.entries(newHU.selectedItems)
      .filter(([_, qty]) => qty > 0)
      .map(([itemNumber, quantity]) => ({
        delivery_number: selectedDelivery.delivery_number,
        delivery_item: parseInt(itemNumber),
        quantity: parseFloat(quantity)
      }));

    if (selectedItems.length === 0) {
      toast.error('Please select items to pack');
      return;
    }

    // Validate quantities
    for (const item of selectedItems) {
      const packingItem = packingItems.find(p => p.item_number === item.delivery_item);
      if (packingItem && item.quantity > packingItem.remaining_quantity) {
        toast.error(`Quantity exceeds remaining for item ${item.delivery_item}`);
        return;
      }
    }

    setIsSaving(true);
    try {
      const payload = {
        delivery_number: selectedDelivery.delivery_number,
        handling_units: [{
          hu_type: newHU.hu_type,
          contents: selectedItems,
          length: newHU.length ? parseFloat(newHU.length) : null,
          width: newHU.width ? parseFloat(newHU.width) : null,
          height: newHU.height ? parseFloat(newHU.height) : null,
          gross_weight: newHU.gross_weight ? parseFloat(newHU.gross_weight) : null,
          net_weight: newHU.net_weight ? parseFloat(newHU.net_weight) : null,
          packaging_material: newHU.packaging_material || null,
          created_by: 'PACKER'
        }],
        created_by: 'PACKER'
      };

      const response = await axios.post(`${BACKEND_URL}/api/warehouse/packing`, payload);
      
      toast.success('Handling Unit created successfully!');
      
      // Reload packing data
      await loadDeliveryPacking(selectedDelivery);
      
      // Reset form
      setNewHU({
        hu_type: 'BOX',
        length: '',
        width: '',
        height: '',
        gross_weight: '',
        net_weight: '',
        packaging_material: '',
        selectedItems: {}
      });
    } catch (error) {
      console.error('Error creating HU:', error);
      toast.error(error.response?.data?.detail || 'Failed to create handling unit');
    } finally {
      setIsSaving(false);
    }
  };

  const getPackingProgress = () => {
    if (!packingItems.length) return 0;
    const totalQty = packingItems.reduce((sum, item) => sum + item.quantity, 0);
    const packedQty = packingItems.reduce((sum, item) => sum + item.packed_quantity, 0);
    return totalQty > 0 ? (packedQty / totalQty) * 100 : 0;
  };

  const isFullyPacked = () => {
    return packingItems.every(item => item.remaining_quantity <= 0);
  };

  const handleProceedToShipment = () => {
    navigate(`/warehouse/shipments/create?delivery=${selectedDelivery.delivery_number}`);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="packing-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/warehouse/pick-lists')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-7 h-7 text-orange-600" />
                Packing
              </h1>
              <p className="text-sm text-gray-500">Create handling units for delivery items</p>
            </div>
          </div>
          {selectedDelivery && isFullyPacked() && (
            <Button 
              onClick={handleProceedToShipment}
              className="bg-green-600 hover:bg-green-700"
            >
              <Truck className="w-4 h-4 mr-2" /> Proceed to Shipment
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          </div>
        ) : !selectedDelivery ? (
          /* Delivery Selection */
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Select Delivery to Pack</h3>
            {deliveries.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No deliveries ready for packing</p>
                <p className="text-sm text-gray-400 mt-1">Complete picking first</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {deliveries.map(delivery => (
                  <div
                    key={delivery.delivery_number}
                    className="p-4 border rounded-lg cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-all"
                    onClick={() => loadDeliveryPacking(delivery)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{delivery.delivery_number}</span>
                      <Badge variant="outline">{delivery.picking_status || 'Ready'}</Badge>
                    </div>
                    <p className="text-sm text-gray-600">SO: {delivery.sales_order_number}</p>
                    <p className="text-sm text-gray-500">Items: {delivery.items?.length || 0}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Delivery Info & Progress */}
            <div className="space-y-6">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Delivery</h3>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDelivery(null)}>
                    Change
                  </Button>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery #:</span>
                    <span className="font-medium">{selectedDelivery.delivery_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sales Order:</span>
                    <span className="font-medium">{selectedDelivery.sales_order_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ship-to:</span>
                    <span className="font-medium">{selectedDelivery.ship_to_party_name || selectedDelivery.ship_to_party}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Packing Progress</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Completion</span>
                      <span className="font-medium">{getPackingProgress().toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-orange-600 h-3 rounded-full transition-all"
                        style={{ width: `${getPackingProgress()}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Handling Units:</span>
                    <span className="font-medium">{handlingUnits.length}</span>
                  </div>
                  {isFullyPacked() && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">Fully Packed!</span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Existing HUs */}
              {handlingUnits.length > 0 && (
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Handling Units</h3>
                  <div className="space-y-3">
                    {handlingUnits.map((hu, index) => (
                      <div key={hu.hu_number || index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Box className="w-4 h-4 text-orange-600" />
                            <span className="font-medium text-sm">{hu.hu_number}</span>
                          </div>
                          <Badge>{HU_TYPES.find(t => t.code === hu.hu_type)?.name || hu.hu_type}</Badge>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          {hu.contents?.length || 0} item(s) | {hu.gross_weight || '-'} kg
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Middle Column - Items to Pack */}
            <div className="lg:col-span-1">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Items to Pack</h3>
                <div className="space-y-3">
                  {packingItems.map(item => (
                    <div 
                      key={item.item_number}
                      className={`p-3 border rounded-lg ${item.remaining_quantity <= 0 ? 'bg-green-50 border-green-200' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{item.material_id}</p>
                          <p className="text-xs text-gray-500">{item.description}</p>
                        </div>
                        {item.remaining_quantity <= 0 && (
                          <Check className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="mt-2 flex justify-between text-xs">
                        <span>Total: {item.quantity} {item.unit_of_measure}</span>
                        <span className={item.remaining_quantity > 0 ? 'text-amber-600' : 'text-green-600'}>
                          Remaining: {item.remaining_quantity}
                        </span>
                      </div>
                      {item.remaining_quantity > 0 && (
                        <div className="mt-2">
                          <Label className="text-xs">Pack Qty:</Label>
                          <Input
                            type="number"
                            min="0"
                            max={item.remaining_quantity}
                            step="0.01"
                            value={newHU.selectedItems[item.item_number] || ''}
                            onChange={(e) => handleItemSelect(item.item_number, e.target.value)}
                            className="h-8 mt-1"
                            placeholder={`Max: ${item.remaining_quantity}`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Right Column - Create HU Form */}
            <div>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Create Handling Unit</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label>HU Type *</Label>
                    <select
                      value={newHU.hu_type}
                      onChange={(e) => setNewHU({ ...newHU, hu_type: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {HU_TYPES.map(type => (
                        <option key={type.code} value={type.code}>{type.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Length (cm)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newHU.length}
                        onChange={(e) => setNewHU({ ...newHU, length: e.target.value })}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Width (cm)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newHU.width}
                        onChange={(e) => setNewHU({ ...newHU, width: e.target.value })}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Height (cm)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newHU.height}
                        onChange={(e) => setNewHU({ ...newHU, height: e.target.value })}
                        className="h-8"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <Weight className="w-3 h-3" /> Gross Weight (kg)
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newHU.gross_weight}
                        onChange={(e) => setNewHU({ ...newHU, gross_weight: e.target.value })}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Net Weight (kg)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newHU.net_weight}
                        onChange={(e) => setNewHU({ ...newHU, net_weight: e.target.value })}
                        className="h-8"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Packaging Material</Label>
                    <Input
                      type="text"
                      value={newHU.packaging_material}
                      onChange={(e) => setNewHU({ ...newHU, packaging_material: e.target.value })}
                      className="h-8"
                      placeholder="e.g., Cardboard, Plastic wrap"
                    />
                  </div>

                  <Button 
                    onClick={handleCreateHU}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={isSaving || Object.values(newHU.selectedItems).filter(q => q > 0).length === 0}
                  >
                    {isSaving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-2" /> Create Handling Unit</>
                    )}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PackingPage;
